import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Permission } from "@/types";

const ACTIONS = ["read", "write", "delete", "update", "manage", "execute"] as const;
const ACCESS_LEVELS = [
  { value: "global", label: "Global" },
  { value: "org",    label: "Org" },
  { value: "user",   label: "User" },
  { value: "page",   label: "Page" },
] as const;

function buildPermValue(type: "tab" | "resource", resource: string, action: string): string {
  const r = resource.trim().toLowerCase().replace(/\s+/g, "_");
  if (type === "tab") return `tab:${r}`;
  return `${r}:${action.trim().toLowerCase()}`;
}

function buildLabel(type: "tab" | "resource", resource: string, action: string): string {
  const r = resource.trim();
  if (!r) return "";
  if (type === "tab") return `${r.charAt(0).toUpperCase() + r.slice(1)} Tab`;
  const a = action.trim();
  if (!a) return r;
  return `${a.charAt(0).toUpperCase() + a.slice(1)} ${r.charAt(0).toUpperCase() + r.slice(1)}`;
}

const SCOPE_STYLES: Record<string, string> = {
  global: "bg-blue-50 text-blue-700",
  org:    "bg-green-50 text-green-700",
  user:   "bg-purple-50 text-purple-700",
  page:   "bg-amber-50 text-amber-700",
};

export function PermissionsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["permissions"],
    queryFn: api.listPermissions,
  });
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");

  const permissions = data?.permissions ?? [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? permissions.filter(
        (p) =>
          p.label.toLowerCase().includes(q) ||
          p.value.toLowerCase().includes(q) ||
          (p.resource ?? "").toLowerCase().includes(q) ||
          (p.action ?? "").toLowerCase().includes(q),
      )
    : permissions;
  const tabPerms = filtered.filter((p) => p.value.startsWith("tab:"));
  const resourcePerms = filtered.filter((p) => !p.value.startsWith("tab:"));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-near-black">Permissions</h2>
              <p className="mt-1 text-sm text-text-secondary">
                All permission tokens — what resource, action, and scope each one covers.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <svg
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                    clipRule="evenodd"
                  />
                </svg>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search permissions…"
                  className="rounded-btn border border-border-card bg-white py-2 pl-9 pr-4 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
                />
              </div>
              <button
                onClick={() => setShowAdd(true)}
                className="flex items-center gap-1.5 rounded-btn bg-near-black px-4 py-2 text-sm font-semibold text-white hover:bg-black transition-colors"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                </svg>
                Add Permission
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
      <div className="overflow-x-auto rounded-card bg-white shadow-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-secondary">
              <th className="py-3 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Label
              </th>
              <th className="py-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Key
              </th>
              <th className="py-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Type
              </th>
              <th className="py-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Resource / Page
              </th>
              <th className="py-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Action
              </th>
              <th className="py-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Scope
              </th>
              <th className="w-24 py-3 pr-4 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-sm text-text-secondary">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-sm text-text-secondary">
                  No permissions match "{search}"
                </td>
              </tr>
            ) : (
              <>
                {tabPerms.length > 0 && (
                  <>
                    <SectionRow label="Tab Access" />
                    {tabPerms.map((p) => (
                      <PermRow key={p.id} perm={p} queryClient={queryClient} />
                    ))}
                  </>
                )}
                {resourcePerms.length > 0 && (
                  <>
                    <SectionRow label="Resource Access" />
                    {resourcePerms.map((p) => (
                      <PermRow key={p.id} perm={p} queryClient={queryClient} />
                    ))}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
        </div>
      </div>

      {showAdd && (
        <AddPermissionModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ["permissions"] });
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
}

function SectionRow({ label }: { label: string }) {
  return (
    <tr className="bg-surface-secondary">
      <td colSpan={7} className="py-1.5 pl-5 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
        {label}
      </td>
    </tr>
  );
}

function PermRow({
  perm,
  queryClient,
}: {
  perm: Permission;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => api.deletePermission(perm.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["permissions"] }),
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => api.togglePermission(perm.id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["permissions"] }),
  });

  const handleDelete = async () => {
    if (!confirm(`Delete permission "${perm.value}"?\nIt will be removed from all roles immediately.`)) return;
    setDeleting(true);
    try {
      await deleteMutation.mutateAsync();
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      await toggleMutation.mutateAsync(!perm.enabled);
    } finally {
      setToggling(false);
    }
  };

  const isTab = perm.value.startsWith("tab:");
  const isEnabled = Boolean(perm.enabled);

  return (
    <tr className="border-b border-surface-secondary last:border-0 hover:bg-surface-secondary/40">
      <td className="py-2.5 pl-5 pr-3 font-medium text-near-black">{perm.label}</td>
      <td className="py-2.5 pr-3">
        <code className="rounded bg-surface-secondary px-1.5 py-0.5 text-[11px] text-text-secondary">
          {perm.value}
        </code>
      </td>
      <td className="py-2.5 pr-3">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
          isTab ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
        }`}>
          {isTab ? "Tab" : "Resource"}
        </span>
      </td>
      <td className="py-2.5 pr-3 text-near-black">{perm.resource}</td>
      <td className="py-2.5 pr-3">
        <span className="rounded-full bg-surface-secondary px-2 py-0.5 text-[11px] font-medium text-text-secondary capitalize">
          {perm.action}
        </span>
      </td>
      <td className="py-2.5 pr-3">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${SCOPE_STYLES[perm.access_level] ?? "bg-surface-secondary text-text-secondary"}`}>
          {perm.access_level}
        </span>
      </td>
      <td className="py-2.5 pr-4">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={handleToggle}
            disabled={toggling}
            title={isEnabled ? "Disable permission" : "Enable permission"}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
              isEnabled ? "bg-green-500" : "bg-surface-secondary"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                isEnabled ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
          {!perm.is_system && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-rose-50 hover:text-error-text disabled:opacity-50 transition-colors"
              title="Delete permission"
            >
              {deleting ? (
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                </svg>
              ) : (
                <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function AddPermissionModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [type, setType] = useState<"tab" | "resource">("resource");
  const [resource, setResource] = useState("");
  const [action, setAction] = useState("read");
  const [accessLevel, setAccessLevel] = useState("org");
  const [labelOverride, setLabelOverride] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const autoValue = buildPermValue(type, resource, type === "tab" ? "access" : action);
  const autoLabel = buildLabel(type, resource, type === "tab" ? "access" : action);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resource.trim()) return;
    setError("");
    setSaving(true);
    try {
      await api.createPermission({
        value: autoValue,
        label: labelOverride.trim() || autoLabel,
        resource: resource.trim().toLowerCase().replace(/\s+/g, "_"),
        action: type === "tab" ? "access" : action,
        access_level: accessLevel,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-card bg-white shadow-card flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-4 border-b border-surface-secondary">
          <h3 className="text-lg font-semibold text-near-black">Add Permission</h3>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Type</label>
                <select
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value as "tab" | "resource");
                    setLabelOverride("");
                    setAccessLevel(e.target.value === "tab" ? "page" : "org");
                  }}
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                >
                  <option value="resource">Resource Access</option>
                  <option value="tab">Tab Access</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Scope</label>
                <select
                  value={accessLevel}
                  onChange={(e) => setAccessLevel(e.target.value)}
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                >
                  {ACCESS_LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`grid gap-3 ${type === "resource" ? "grid-cols-2" : "grid-cols-1"}`}>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  {type === "tab" ? "Tab / Page name" : "Resource"}
                </label>
                <input
                  type="text"
                  value={resource}
                  onChange={(e) => { setResource(e.target.value); setLabelOverride(""); }}
                  placeholder={type === "tab" ? "e.g. reports" : "e.g. invoices"}
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                  autoFocus
                  required
                />
              </div>
              {type === "resource" && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Action</label>
                  <select
                    value={action}
                    onChange={(e) => { setAction(e.target.value); setLabelOverride(""); }}
                    className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                  >
                    {ACTIONS.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Label{" "}
                <span className="font-normal normal-case text-text-secondary">(auto-generated)</span>
              </label>
              <input
                type="text"
                value={labelOverride || autoLabel}
                onChange={(e) => setLabelOverride(e.target.value)}
                placeholder="Display label"
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              />
            </div>

            <div className="rounded-btn bg-surface-secondary px-3 py-2">
              <p className="text-xs text-text-secondary">
                Permission key:{" "}
                <code className="font-mono font-medium text-near-black">{autoValue || "…"}</code>
              </p>
            </div>

            {error && (
              <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-surface-secondary px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-btn border border-border-card bg-white px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !resource.trim()}
              className="rounded-btn bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving…" : "Create Permission"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
